import React, { createContext, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useAgentPanel } from './hooks/useAgentPanel'

const AgentPanelStableContext = createContext(null)
const AgentPanelDynamicContext = createContext(null)

export function AgentPanelProvider ({
  agentId,
  workspaceContext,
  onApplySource,
  onApplyTest,
  children
}) {
  const panel = useAgentPanel(agentId, { workspaceContext })
  const [autoAccept, setAutoAccept] = useState(true)

  const stable = useMemo(() => ({
    addMessage: panel.addMessage,
    resumeThread: panel.resumeThread,
    config: panel.config,
    onApplySource,
    onApplyTest,
    autoAccept,
    setAutoAccept,
    isEditorMode: !!workspaceContext?.source,
    testsRef: workspaceContext?.testsRef || null
  }), [panel.addMessage, panel.resumeThread, panel.config, onApplySource, onApplyTest, autoAccept, workspaceContext?.source, workspaceContext?.testsRef])

  const dynamic = useMemo(() => ({
    messages: panel.messages,
    agentStatus: panel.agentStatus,
    isPendingAction: panel.isPendingAction,
    isResuming: panel.isResuming,
    interrupts: panel.interrupts,
    processingMessage: panel.processingMessage,
    isThreadLoading: panel.isThreadLoading,
    isErrored: panel.isErrored,
    errorMessage: panel.errorMessage,
    threadId: panel.threadId,
    threadData: panel.threadData,
    currentSource: workspaceContext?.source || ''
  }), [panel.messages, panel.agentStatus, panel.isPendingAction, panel.isResuming, panel.interrupts, panel.processingMessage, panel.isThreadLoading, panel.isErrored, panel.errorMessage, panel.threadId, panel.threadData, workspaceContext?.source])

  return (
    <AgentPanelStableContext.Provider value={stable}>
      <AgentPanelDynamicContext.Provider value={dynamic}>
        {children}
      </AgentPanelDynamicContext.Provider>
    </AgentPanelStableContext.Provider>
  )
}

AgentPanelProvider.propTypes = {
  agentId: PropTypes.string.isRequired,
  workspaceContext: PropTypes.object,
  onApplySource: PropTypes.func,
  onApplyTest: PropTypes.func,
  children: PropTypes.node
}

export function useAgentPanelContext () {
  const stable = useContext(AgentPanelStableContext)
  const dynamic = useContext(AgentPanelDynamicContext)
  if (!stable || !dynamic) {
    throw new Error('useAgentPanelContext must be used within an AgentPanelProvider')
  }
  return { ...stable, ...dynamic }
}

export function useAgentPanelStable () {
  const ctx = useContext(AgentPanelStableContext)
  if (!ctx) throw new Error('useAgentPanelStable must be used within an AgentPanelProvider')
  return ctx
}

export function useAgentPanelDynamic () {
  const ctx = useContext(AgentPanelDynamicContext)
  if (!ctx) throw new Error('useAgentPanelDynamic must be used within an AgentPanelProvider')
  return ctx
}
